import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { useTypedSelector } from '../../../shared/hooks';
import AlertService from '../../../shared/services/AlertService';
import { alertQueueActions } from '../../../shared/store/alertQueueSlice';
import { type RootState } from '../../../shared/types';
import formatReportType from '../../../shared/utils/reportType';
import { useCreateAsyncExportMutation } from '../data-layer/violationsReportApi';
import { type CreateAsyncExportRequest } from '../data-layer/violationsReportTypes';
import { violationsReportActions } from '../data-store/violationsReportSlice';

const useExportReport = () => {
  const dispatch = useDispatch();
  const [createAsyncExport, { isLoading }] = useCreateAsyncExportMutation();
  // Legacy local timeouts removed

  const { activeExports } = useTypedSelector(
    (state: RootState) => state.aag.report
  );

  const toTitleCaseReportType = useCallback(
    (rt: string) => formatReportType(rt),
    []
  );

  const showSuccessBanner = useCallback(
    (reportType: string) => {
      // Use centralized banner manager for Exporting info
      dispatch(
        alertQueueActions.enqueue(
          AlertService.info(
            'Exporting',
            `${toTitleCaseReportType(
              reportType
            )} export is running in background. You'll receive an email when your report is ready. You can also download it later from My Downloads in your profile.`,
            2000
          )
        )
      );
    },
    [toTitleCaseReportType, dispatch]
  );

  const showErrorBanner = useCallback(
    (reportType: string, apiMessage?: string) => {
      // Route generic error to banner manager
      const message =
        apiMessage && apiMessage.trim().length > 0
          ? apiMessage
          : `${toTitleCaseReportType(
              reportType
            )} export failed. Please try again or contact support if the issue persists.`;

      dispatch(
        alertQueueActions.enqueue(
          AlertService.error(
            'Export failed',
            message,
            5000,
            `export-error-${reportType}`
          )
        )
      );
    },
    [toTitleCaseReportType, dispatch]
  );

  // closeBanner no longer needed (centralized banners)

  const exportReport = useCallback(
    async (payload: CreateAsyncExportRequest) => {
      // Check if this report type is already being exported
      if (
        Array.isArray(activeExports) &&
        activeExports.some((e) => e.reportType === payload.reportType)
      ) {
        // Show a message that export is already in progress (centralized)
        dispatch(
          alertQueueActions.enqueue(
            AlertService.info(
              'Export in Progress',
              `${toTitleCaseReportType(
                payload.reportType
              )} export is already in progress. Please wait for it to complete.`,
              2000
            )
          )
        );

        return;
      }

      try {
        // Reset this reportType's progress to 0 so UI starts fresh
        dispatch(
          violationsReportActions.setExportProgress({
            reportType: payload.reportType,
            progressPercentage: 0,
          })
        );

        await createAsyncExport(payload).unwrap();

        // Add to active exports for polling
        dispatch(
          violationsReportActions.addActiveExport({
            reportType: payload.reportType,
            progress: 0,
          })
        );

        showSuccessBanner(payload.reportType);
      } catch (error) {
        Logger.log('error', 'Export failed:', error);

        // Generic RTK Query error messages to skip
        const GENERIC_ERROR_PATTERNS = [
          'Request failed with status code',
          'Failed to create async export',
          'Invalid response format',
        ];

        const isGenericMessage = (msg: string): boolean =>
          GENERIC_ERROR_PATTERNS.some((pattern) => msg.includes(pattern));

        /**
         * Recursively extracts error message from various error structures.
         * Handles RTK Query errors, API responses, and nested error objects.
         */
        const extractErrorMessage = (input: unknown): string | null => {
          // Handle string errors directly
          if (typeof input === 'string') {
            return input.trim().length > 0 ? input.trim() : null;
          }

          // Handle object errors
          if (!input || typeof input !== 'object') {
            return null;
          }

          const obj = input as Record<string, unknown>;

          // Priority 1: Check for message property (most common case)
          if ('message' in obj) {
            const { message } = obj;

            if (typeof message === 'string') {
              const trimmed = message.trim();

              if (trimmed.length > 0 && !isGenericMessage(trimmed)) {
                return trimmed;
              }
            }
          }

          // Priority 2: Check nested data structures
          // RTK Query error format: error.data contains the API response
          // For 400 errors: { data: { status: 400, message: "..." } }
          const dataSources: unknown[] = [];

          if ('data' in obj) {
            dataSources.push(obj.data);
          }

          if (
            'response' in obj &&
            obj.response &&
            typeof obj.response === 'object'
          ) {
            const response = obj.response as Record<string, unknown>;

            if ('data' in response) {
              dataSources.push(response.data);
            }
          }

          // Recursively check all data sources
          const extractedMessage = dataSources
            .map((data) => extractErrorMessage(data))
            .find((msg) => msg !== null);

          if (extractedMessage) {
            return extractedMessage;
          }

          return null;
        };

        // Extract error message from the caught error
        const errorMessage = extractErrorMessage(error) ?? '';

        Logger.log('error', 'Extracted error message:', errorMessage);

        showErrorBanner(payload.reportType, errorMessage);
      }
    },
    [
      createAsyncExport,
      showSuccessBanner,
      showErrorBanner,
      activeExports,
      dispatch,
      toTitleCaseReportType,
    ]
  );

  const isExportDisabled = useCallback(
    (reportType: string) =>
      Array.isArray(activeExports) &&
      activeExports.some((e) => e.reportType === reportType),
    [activeExports]
  );

  // No local timeouts to clean up

  return {
    exportReport,
    isExporting: isLoading,
    // closeBanner removed
    isExportDisabled,
    activeExports: Array.isArray(activeExports) ? activeExports : [],
  };
};

export default useExportReport;
